'use client';

import { useBunja } from 'bunja/react';
import { useEffect, useRef, useState } from 'react';

import { css, cx } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';
import { getUserAgentInfo } from '@flipster/utils/user-agent';

import { competitionBunja } from '../state';
import MusicOffIcon from './assets/music-off.svg';
import MusicOnIcon from './assets/music-on.svg';

export const BgmToggle = () => {
  const { competitionContentful } = useBunja(competitionBunja);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const initializedRef = useRef(false);
  const rotationHostRef = useRef<HTMLSpanElement | null>(null);
  const rotationAnimRef = useRef<Animation | null>(null);
  const rotationAnimHostRef = useRef<HTMLSpanElement | null>(null);
  const { isIos } = getUserAgentInfo();

  const bgmUrl = competitionContentful?.bgm?.url ?? '';

  // Try autoplay on mount if not iOS and default isPlaying=true
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    if (!bgmUrl) return;
    if (isIos) {
      setIsPlaying(false);
      return;
    }
    // Attempt autoplay
    (async () => {
      try {
        if (!audioRef.current) {
          const audio = new Audio(bgmUrl);
          audio.loop = true;
          audio.preload = 'auto';
          audioRef.current = audio;
        }
        await audioRef.current.play();
        setIsPlaying(true);
      } catch {
        setIsPlaying(false);
      }
    })();
  }, [bgmUrl, isIos]);

  useEffect(() => {
    return () => {
      // cleanup on unmount
      try {
        audioRef.current?.pause();
      } catch {
        // no-op
      }
      audioRef.current = null;
      try {
        rotationAnimRef.current?.cancel();
      } catch {
        // no-op
      }
      rotationAnimRef.current = null;
    };
  }, []);

  /* moved below hooks to satisfy hooks rules */

  const toggle = async () => {
    try {
      if (!audioRef.current) {
        const audio = new Audio(bgmUrl);
        audio.loop = true;
        audio.preload = 'auto';
        audioRef.current = audio;
      }
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch {
      // play can fail without user gesture / browser policy
      setIsPlaying(false);
    }
  };

  // rotate MusicOnIcon while playing using Web Animations API
  useEffect(() => {
    const host = rotationHostRef.current;
    // if host element changed (due to conditional render), reset the previous animation
    if (rotationAnimHostRef.current && rotationAnimHostRef.current !== host) {
      try {
        rotationAnimRef.current?.cancel();
      } catch {
        // no-op
      }
      rotationAnimRef.current = null;
    }
    rotationAnimHostRef.current = host || null;

    if (!host) return;
    if (isPlaying) {
      if (!rotationAnimRef.current) {
        rotationAnimRef.current = host.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }], {
          duration: 1200,
          iterations: Infinity,
          easing: 'linear',
        });
      } else {
        try {
          rotationAnimRef.current.play();
        } catch {
          rotationAnimRef.current = host.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }], {
            duration: 1200,
            iterations: Infinity,
            easing: 'linear',
          });
        }
      }
    } else {
      try {
        rotationAnimRef.current?.pause();
      } catch {
        rotationAnimRef.current?.cancel();
        rotationAnimRef.current = null;
      }
    }
  }, [isPlaying]);

  if (!bgmUrl) return null;

  return (
    <div
      className={center({
        pos: 'absolute',
        top: { base: 12, tablet: 40 },
        right: { base: 12, tablet: 80 },
        zIndex: 2,
      })}
    >
      <button
        type='button'
        aria-label={isPlaying ? 'Pause BGM' : 'Play BGM'}
        title={isPlaying ? 'Pause BGM' : 'Play BGM'}
        onClick={toggle}
        className={center({
          rounded: 9999,
          bgColor: 'rgba(0,0,0,0.35)',
          color: 'inverse.white',
          _hover: { bgColor: 'rgba(0,0,0,0.5)' },
          backdropFilter: 'blur(6px)',
          cursor: 'pointer',
        })}
      >
        {isPlaying ? (
          <span
            ref={rotationHostRef}
            className={cx(
              iconWrapper,
              css({ position: 'relative', display: 'inline-flex', transformOrigin: '50% 50%' }),
            )}
          >
            <MusicOnIcon />
          </span>
        ) : (
          <span className={iconWrapper}>
            <MusicOffIcon />
          </span>
        )}
      </button>
    </div>
  );
};

const iconWrapper = center({
  bgColor: 'content.accent.info.hover',
  rounded: '50%',
  w: 58,
  h: 58,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'inverse.white',
});
